import { authApi } from './api';
import { salvarTokens, encerrarSessao } from './sessao';
import type {
  LoginInput, 
  LoginResponse, 
  LoginMfaRequerido,
  LoginResultado,
  Usuario,
  EsqueciSenhaInput,
  RedefinirSenhaInput,
  AlterarSenhaInput,
  VerificarCodigoInput,
  ForgotPasswordResponse,
  VerifyCodeResponse,
  ResetPasswordResponse,
  ChangePasswordResponse,
  LogoutResponse,
  MfaStatusResponse,
  TotpEnableResponse,
  VerifyTotpInput,
  DisableMfaInput,
  SendOtpInput,
  VerifyOtpInput,
} from '@/tipos';

export const authServico = {
  /**
   * Realiza login. Conta com MFA responde primeiro sem sessão; a tela pede o
   * código e chama de novo com `mfa_codigo`.
   */
  async login(dados: LoginInput): Promise<LoginResultado> {
    const response = await authApi.post<LoginResponse | LoginMfaRequerido>('/login', dados);
    const corpo = response.data;

    // Sem o código, o auth responde 200 só com `require_mfa`. Sem esta
    // conferência a web gravava os tokens "undefined" e quebrava com TypeError
    // ao ler o usuário; o app já pedia o código.
    if ('require_mfa' in corpo) {
      return { mfaRequerido: true };
    }
    if (!corpo.access_token || !corpo.user) {
      throw new Error('Não foi possível concluir a entrada. Tente novamente em instantes.');
    }

    // Persiste tokens + validade e agenda o refresh proativo (BUG-010).
    salvarTokens({
      access_token: corpo.access_token,
      refresh_token: corpo.refresh_token,
      expires_in: corpo.expires_in,
    });
    localStorage.setItem('user', JSON.stringify(corpo.user));

    return { mfaRequerido: false, usuario: corpo.user };
  },

  /**
   * Sai só deste navegador. A sessão local é limpa mesmo se a chamada falhar:
   * sair daqui é uma ação local, e a falha de rede não pode prender ninguém.
   */
  async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await authApi.post<LogoutResponse>('/logout', { refresh_token: refreshToken });
      }
    } finally {
      // Cancela o refresh proativo, limpa a sessão e propaga o logout às abas.
      encerrarSessao();
    }
  },

  /**
   * Sai de todos os aparelhos: o /logout-all revoga todas as sessões e corta os
   * access tokens já emitidos, inclusive o deste navegador.
   *
   * Ao contrário do `logout`, a sessão local só é limpa depois que o servidor
   * confirmou. Revogar lá é o ponto da ação: limpando aqui mesmo com a falha, a
   * pessoa veria a tela de login e acharia que encerrou tudo, com as outras
   * sessões ainda válidas. É a mesma regra do app.
   */
  async logoutTodos(): Promise<void> {
    await authApi.post<LogoutResponse>('/logout-all');
    encerrarSessao();
  },

  /**
   * Obtém dados do usuário logado
   */
  async me(): Promise<Usuario> {
    const response = await authApi.get<Usuario>('/me');
    return response.data;
  },

  /**
   * Solicita recuperação de senha
   */
  async esqueciSenha(dados: EsqueciSenhaInput): Promise<ForgotPasswordResponse> {
    const response = await authApi.post<ForgotPasswordResponse>('/forgot-password', dados);
    return response.data;
  },

  /**
   * Verifica código de recuperação de senha
   */
  async verificarCodigo(dados: VerificarCodigoInput): Promise<VerifyCodeResponse> {
    const response = await authApi.post<VerifyCodeResponse>('/verify-code', dados);
    return response.data;
  },

  /**
   * Redefine senha com código
   */
  async redefinirSenha(dados: RedefinirSenhaInput): Promise<ResetPasswordResponse> {
    const response = await authApi.post<ResetPasswordResponse>('/reset-password', dados);
    return response.data;
  },

  /**
   * Altera senha do usuário logado
   */
  async alterarSenha(dados: AlterarSenhaInput): Promise<ChangePasswordResponse> {
    const response = await authApi.post<ChangePasswordResponse>('/change-password', dados);
    encerrarSessao();
    return response.data;
  },

  // ==========================================
  // MFA / 2FA - TOTP (Google Authenticator)
  // ==========================================

  /**
   * Habilita TOTP (Time-based One-Time Password)
   * Retorna QR Code para escanear com Google Authenticator
   */
  async habilitarTOTP(): Promise<TotpEnableResponse> {
    const response = await authApi.post<TotpEnableResponse>('/mfa/totp/enable');
    return response.data;
  },

  /**
   * Verifica código TOTP para ativar MFA
   */
  async verificarTOTP(dados: VerifyTotpInput): Promise<{ success: boolean; message: string }> {
    const response = await authApi.post<{ success: boolean; message: string }>('/mfa/totp/verify', dados);
    return response.data;
  },

  /**
   * Desabilita TOTP (requer confirmação de senha)
   */
  async desabilitarTOTP(dados: DisableMfaInput): Promise<{ success: boolean; message: string }> {
    const response = await authApi.post<{ success: boolean; message: string }>('/mfa/totp/disable', dados);
    return response.data;
  },

  // ==========================================
  // MFA / 2FA - OTP (Email/SMS)
  // ==========================================

  /**
   * Envia código OTP via email ou SMS
   */
  async enviarOTP(dados: SendOtpInput): Promise<ForgotPasswordResponse> {
    const response = await authApi.post<ForgotPasswordResponse>('/mfa/otp/send', dados);
    return response.data;
  },

  /**
   * Verifica código OTP
   */
  async verificarOTP(dados: VerifyOtpInput): Promise<VerifyCodeResponse> {
    const response = await authApi.post<VerifyCodeResponse>('/mfa/otp/verify', dados);
    return response.data;
  },

  /**
   * Obtém status do MFA do usuário
   */
  async obterStatusMFA(): Promise<MfaStatusResponse> {
    const response = await authApi.get<MfaStatusResponse>('/mfa/status');
    return response.data;
  },

  /**
   * Obtém usuário do localStorage
   */
  getUsuarioLocal(): Usuario | null {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  },

  /**
   * Verifica se está autenticado
   */
  isAutenticado(): boolean {
    return !!localStorage.getItem('access_token');
  },

  /**
   * Obtém token de acesso
   */
  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  },
};
